import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type PageTopBarPartial = {
  breadcrumbs?: React.ReactNode | null;
  /** The record the page shows, for the browser tab's title (PageHeader's `recordTitle`). */
  recordTitle?: string | null;
};

interface PageTopBarContextValue {
  breadcrumbs: React.ReactNode | null;
  recordTitle: string | null;
  setPageTopBar: (partial: PageTopBarPartial) => void;
}

const PageTopBarContext = createContext<PageTopBarContextValue | null>(null);

export function PageTopBarProvider({ children }: { children: React.ReactNode }) {
  const [breadcrumbs, setBreadcrumbs] = useState<React.ReactNode | null>(null);
  const [recordTitle, setRecordTitle] = useState<string | null>(null);

  const setPageTopBar = useCallback(
    (partial: PageTopBarPartial) => {
      if ('breadcrumbs' in partial) {
        setBreadcrumbs((prev) =>
          partial.breadcrumbs === prev ? prev : (partial.breadcrumbs ?? null)
        );
      }
      if ('recordTitle' in partial) setRecordTitle(partial.recordTitle ?? null);
    },
    []
  );

  const value = useMemo(
    () => ({
      breadcrumbs,
      recordTitle,
      setPageTopBar,
    }),
    [breadcrumbs, recordTitle, setPageTopBar]
  );

  return (
    <PageTopBarContext.Provider value={value}>
      <div
        data-slot="app-shell"
        className="flex min-h-0 w-full flex-1 flex-col [--app-topbar-height:3rem]"
      >
        {children}
      </div>
    </PageTopBarContext.Provider>
  );
}

export function usePageTopBarOptional() {
  return useContext(PageTopBarContext);
}
